import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { clsx } from "clsx";
import type { DocHandle } from "../sync/docManager";
import type { FilterAst } from "../core/filter";
import type { Task } from "../core/schema";
import { useDocSelector } from "../core/useDocSelector";
import { fmtRelativeAbsolute } from "../core/format";
import { visibleTasks } from "../views/shared";
import { useElsewhere } from "../views/elsewhere/useElsewhere";
import type { Source } from "../views/elsewhere/source";
import {
  dayKey,
  dragged,
  dropPlan,
  dropUnplan,
  planTask,
  readPlanner,
  startDrag,
  unplanTask,
  type PlanItem,
} from "./ops";
import { allDayOn, startingAt, timeRange, useCalendarEvents, type CalendarEvent } from "./calendarEvents";

/** Below this width the trays sit under the grid (page) or are left out (widget). */
const TRAYS_BESIDE_PX = 640;
/** The tray of open tasks: newest first (what was just captured is what is most likely planned next), at most this many. */
const TRAY_LIMIT = 40;

export interface PlannerProps {
  handle: DocHandle | null;
  workspaceId: string;
  readOnly: boolean;
  /** the days shown, `YYYY-MM-DD` */
  days: string[];
  /** the hours shown, each a row */
  hours: number[];
  /** one day as a list of hours, or several days as a grid */
  layout: "day" | "week";
  todayKey: string;
  now: number;
  filter?: FilterAst;
  /** "Show from" (ADR 0024): other workspaces' planned and due tasks in the grid, named, read-only */
  source?: Source;
  showCalendars?: boolean;
  showDue?: boolean;
  /** narrow: the trays go under the grid (a page) or are left out (a widget, which has a fixed height) */
  narrowTrays?: "below" | "hidden";
  openTask(id: string, workspaceId?: string): void;
  openDay?(day: string): void;
}

/**
 * The planner (spec §9.6 timeboxing), one component for the Planner page and the Week widget (UI cleanup U2):
 * hours down, days across; drag a task from the trays onto an hour, a block to another hour or day, or a block back
 * onto the trays to take it off its day. The trays hold the tasks not planned in the shown days: due in them, the
 * other open ones (newest first), then *someday*. A dropped task gets its estimate as the block length (an hour
 * without one). Due tasks also show in the grid at their time, and calendars as read-only appointments.
 */
export function Planner(p: PlannerProps) {
  const { t, i18n } = useTranslation();
  const { handle, days, hours, layout, todayKey, now } = p;
  const ro = p.readOnly || !handle;
  const filterKey = JSON.stringify(p.filter ?? null);
  const data = useDocSelector(
    handle,
    (doc) => {
      const v = visibleTasks(doc, p.filter, Date.now(), { includeSomeday: true, includeDone: true });
      return {
        tasks: v.tasks,
        roots: new Set(v.tasks.filter((x) => v.snapshot.parentOf(x.id) === null).map((x) => x.id)),
        plan: readPlanner(doc),
      };
    },
    [filterKey],
  );
  const elsewhere = useElsewhere(p.workspaceId, p.source ?? "here", p.filter, now);
  const after = dayKey(new Date(new Date(days[days.length - 1] + "T12:00:00").getTime() + 86_400_000));
  const events = useCalendarEvents(p.workspaceId, days[0], after, p.showCalendars !== false);
  const byId = useMemo(() => new Map((data?.tasks ?? []).map((x) => [x.id, x])), [data]);
  const planOf = (day: string) => data?.plan[day] ?? [];
  const planned = new Set(days.flatMap((d) => planOf(d).map((x) => x.taskId)));
  const open = (data?.tasks ?? []).filter(
    (x) => !x.doneAt && data?.roots.has(x.id) && !planned.has(x.id) && x.typeId !== "recurring",
  );
  const shown = new Set(days);
  const dueIn = (x: Task) => typeof x.props.due === "string" && shown.has(dayKey(new Date(x.props.due)));
  const due = open.filter(dueIn);
  const someday = open.filter((x) => x.props.horizon === "someday" && !dueIn(x));
  const rest = open
    .filter((x) => !dueIn(x) && x.props.horizon !== "someday")
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, TRAY_LIMIT);
  const first = hours[0];
  const last = hours[hours.length - 1];
  const hourOf = (x: PlanItem) => {
    const h = x.start ? Number(x.start.slice(0, 2)) : first;
    return Math.min(last, Math.max(first, h));
  };
  /** the block length a task gets when it is dropped: its estimate, else an hour */
  const lengthOf = (id: string) => {
    const est = Number(byId.get(id)?.props.estimateMin);
    return est > 0 ? est : 60;
  };
  const at = (h: number) => `${String(h).padStart(2, "0")}:00`;
  const drop = (e: DragEvent, day: string, h: number) => {
    e.preventDefault();
    if (!handle || ro) return;
    const what = dragged(e.dataTransfer);
    dropPlan(handle.doc, what, day, at(h), lengthOf(what.id));
  };
  const planAt = (id: string, day: string, h: number) =>
    handle && !ro && planTask(handle.doc, day, { taskId: id, start: at(h), minutes: lengthOf(id) });

  // what sits in one hour of one day: appointments, this workspace's blocks, other workspaces' blocks, due tasks
  const cell = (day: string, h: number, compact: boolean): ReactNode[] => {
    const out: ReactNode[] = [];
    if (layout === "day" && h === first)
      for (const e of allDayOn(events, day))
        out.push(<PlannerEvent key={`a-${e.calendarId}-${e.title}`} e={e} lang={i18n.language} />);
    for (const e of startingAt(events, day, h, first, last))
      out.push(
        <PlannerEvent
          key={`${e.calendarId}-${e.title}-${e.start}`}
          e={e}
          lang={i18n.language}
          compact={compact}
        />,
      );
    for (const x of planOf(day).filter((x) => hourOf(x) === h)) {
      const task = byId.get(x.taskId);
      out.push(
        <PlanChip
          key={x.taskId}
          title={task?.title ?? t("task.untitled")}
          done={Boolean(task?.doneAt)}
          minutes={x.minutes ?? 60}
          compact={compact}
          ro={ro}
          onDragStart={(e) => startDrag(e.dataTransfer, x.taskId, day)}
          onOpen={() => p.openTask(x.taskId)}
          onUnplan={() => handle && !ro && unplanTask(handle.doc, day, x.taskId)}
        />,
      );
    }
    for (const w of elsewhere) {
      const ids = new Map(w.tasks.map((x) => [x.id, x]));
      for (const x of (w.planner[day] ?? []).filter((x) => hourOf(x) === h)) {
        const task = ids.get(x.taskId);
        if (task)
          out.push(
            <ForeignChip
              key={`${w.id}-${x.taskId}`}
              title={task.title}
              where={w.name}
              workspace={w.id}
              compact={compact}
              testId="planner-item"
              onOpen={() => p.openTask(x.taskId, w.id)}
            />,
          );
      }
    }
    if (p.showDue !== false) {
      const sources = [
        { id: undefined as string | undefined, name: "", tasks: data?.tasks ?? [] },
        ...elsewhere,
      ];
      for (const w of sources)
        for (const x of w.tasks) {
          if (x.doneAt || typeof x.props.due !== "string") continue;
          const d = new Date(x.props.due);
          if (dayKey(d) !== day || Math.min(last, Math.max(first, d.getHours())) !== h) continue;
          out.push(
            <ForeignChip
              key={`due-${w.id ?? ""}-${x.id}`}
              title={x.title}
              where={w.id ? w.name : undefined}
              workspace={w.id}
              compact={compact}
              due
              testId="planner-due"
              onOpen={() => p.openTask(x.id, w.id)}
            />,
          );
        }
    }
    return out;
  };

  // the trays sit beside the grid when there is room for both
  const box = useRef<HTMLDivElement>(null);
  const [wide, setWide] = useState(true);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setWide(el.clientWidth >= TRAYS_BESIDE_PX + 200);
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const [overTray, setOverTray] = useState(false);
  const showTrays = wide || p.narrowTrays !== "hidden";
  const fmt = (d: string, o: Intl.DateTimeFormatOptions) =>
    new Date(d + "T12:00:00").toLocaleDateString(i18n.language, o);

  return (
    <div ref={box} className={clsx("flex min-h-0 w-full gap-4", wide ? "flex-row" : "flex-col")}>
      <div className="min-h-0 min-w-0 flex-1 overflow-auto">
        {layout === "day" ? (
          <ol className="kss-surface divide-y divide-neutral-200 dark:divide-neutral-800">
            {hours.map((h) => (
              <HourRow
                key={h}
                hour={h}
                day={days[0]}
                role="listitem"
                testId="planner-hour"
                onDrop={(e) => drop(e, days[0], h)}
                className="flex min-h-10 gap-3 px-3 py-1.5"
              >
                <span className="w-10 shrink-0 pt-0.5 font-mono text-[11px] text-neutral-400">{at(h)}</span>
                <div className="flex min-w-0 flex-1 flex-col gap-1">{cell(days[0], h, false)}</div>
              </HourRow>
            ))}
          </ol>
        ) : (
          <div
            className="kss-surface overflow-hidden"
            style={{ "--days": days.length } as CSSProperties}
            data-testid="planner-week"
          >
            <div className="grid grid-cols-[3rem_repeat(var(--days),minmax(0,1fr))] border-b border-neutral-200 dark:border-neutral-800">
              <span />
              {days.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => p.openDay?.(d)}
                  disabled={!p.openDay}
                  title={p.openDay ? t("planner.openDay") : undefined}
                  data-testid="planner-day-head"
                  data-day={d}
                  className={clsx(
                    "border-l border-neutral-200 px-2 py-1.5 text-left text-[12px] enabled:hover:bg-neutral-100 dark:border-neutral-800 dark:enabled:hover:bg-neutral-800",
                    d === todayKey ? "font-semibold text-accent" : "text-neutral-600 dark:text-neutral-300",
                  )}
                >
                  {fmt(d, { weekday: "short" })} {fmt(d, { day: "numeric" })}
                  {d === todayKey && <span className="sr-only"> ({t("calendar.today")})</span>}
                </button>
              ))}
            </div>
            {days.some((d) => allDayOn(events, d).length > 0) && (
              <div className="grid grid-cols-[3rem_repeat(var(--days),minmax(0,1fr))] border-b border-neutral-200 dark:border-neutral-800">
                <span className="px-1 pt-1 text-[10px] text-neutral-400">{t("calendar.allDay")}</span>
                {days.map((d) => (
                  <div
                    key={d}
                    className="flex min-w-0 flex-col gap-0.5 border-l border-neutral-200 p-1 dark:border-neutral-800"
                  >
                    {allDayOn(events, d).map((e) => (
                      <PlannerEvent key={`a-${e.calendarId}-${e.title}`} e={e} lang={i18n.language} compact />
                    ))}
                  </div>
                ))}
              </div>
            )}
            <ol>
              {hours.map((h) => (
                <li
                  key={h}
                  className="grid grid-cols-[3rem_repeat(var(--days),minmax(0,1fr))] border-b border-neutral-200 last:border-b-0 dark:border-neutral-800"
                >
                  <span className="px-2 pt-1 font-mono text-[11px] text-neutral-400">
                    {String(h).padStart(2, "0")}
                  </span>
                  {days.map((d) => (
                    <HourRow
                      key={d}
                      hour={h}
                      day={d}
                      testId="planner-cell"
                      onDrop={(e) => drop(e, d, h)}
                      className={clsx(
                        "flex min-h-9 min-w-0 flex-col gap-0.5 border-l border-neutral-200 p-0.5 dark:border-neutral-800",
                        d === todayKey && "bg-accent-tint/30",
                      )}
                    >
                      {cell(d, h, true)}
                    </HourRow>
                  ))}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
      {showTrays && (
        <aside
          className={clsx(
            "min-h-0 shrink-0 overflow-auto rounded-sm",
            wide ? (layout === "day" ? "w-72" : "w-60") : "w-full",
            overTray && "bg-accent-tint",
          )}
          aria-label={t("calendar.tray")}
          data-testid="planner-trays"
          onDragOver={(e) => {
            e.preventDefault();
            setOverTray(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOverTray(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setOverTray(false);
            if (handle && !ro) dropUnplan(handle.doc, dragged(e.dataTransfer));
          }}
        >
          <Tray
            title={days.length === 1 ? t("planner.dueToday") : t("planner.dueThisWeek")}
            items={due}
            ro={ro}
            now={now}
            days={days}
            hours={hours}
            onPlan={planAt}
          />
          <Tray
            title={t("calendar.tray")}
            items={rest}
            ro={ro}
            now={now}
            days={days}
            hours={hours}
            onPlan={planAt}
          />
          <Tray
            title={t("planner.someday")}
            items={someday}
            ro={ro}
            now={now}
            days={days}
            hours={hours}
            onPlan={planAt}
          />
        </aside>
      )}
    </div>
  );
}

/** A drop target for one hour (of one day), highlighted while something is over it. */
function HourRow({
  hour,
  day,
  testId,
  className,
  onDrop,
  children,
  role,
}: {
  role?: string;
  hour: number;
  day: string;
  testId: string;
  className: string;
  onDrop(e: DragEvent): void;
  children: ReactNode;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      role={role}
      data-testid={testId}
      data-hour={hour}
      data-day={day}
      className={clsx(className, over && "bg-accent-tint")}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        onDrop(e);
      }}
    >
      {children}
    </div>
  );
}

function PlanChip({
  title,
  done,
  minutes,
  compact,
  ro,
  onDragStart,
  onOpen,
  onUnplan,
}: {
  title: string;
  done: boolean;
  minutes: number;
  compact: boolean;
  ro: boolean;
  onDragStart(e: DragEvent): void;
  onOpen(): void;
  onUnplan(): void;
}) {
  const { t } = useTranslation();
  return (
    <div
      draggable={!ro}
      onDragStart={onDragStart}
      data-testid="planner-item"
      title={compact ? `${title} · ${minutes} min` : undefined}
      className={clsx(
        "group flex min-w-0 items-center gap-1.5 rounded-sm border border-accent-tint-2 bg-accent-tint text-accent",
        compact ? "px-1.5 py-0.5 text-[12px]" : "gap-2 px-2 py-1 text-[13px]",
        done && "line-through opacity-60",
      )}
    >
      {/* draggable too: grabbing the title, where most people take hold, starts no drag from a plain button */}
      <button type="button" draggable={!ro} className="min-w-0 flex-1 truncate text-left" onClick={onOpen}>
        {title}
      </button>
      {!compact && <span className="font-mono text-[11px]">{minutes} min</span>}
      {!ro && (
        <button
          type="button"
          aria-label={t("planner.unplan")}
          onClick={onUnplan}
          className={clsx(
            "grid h-5 w-5 shrink-0 place-items-center rounded-sm hover:bg-accent-tint-2",
            compact && "hidden group-hover:grid group-focus-within:grid",
          )}
        >
          <X size={12} aria-hidden />
        </button>
      )}
    </div>
  );
}

/** A due task, or another workspace's block: shown at its time, opened on click, not dragged from here. */
function ForeignChip({
  title,
  where,
  workspace,
  compact,
  due,
  testId,
  onOpen,
}: {
  title: string;
  where?: string;
  workspace?: string;
  compact: boolean;
  due?: boolean;
  testId: string;
  onOpen(): void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testId}
      data-workspace={workspace}
      title={[due ? t("calendar.due") : t("calendar.planned"), where].filter(Boolean).join(" · ")}
      className={clsx(
        "block w-full min-w-0 truncate rounded-sm border text-left",
        compact ? "px-1.5 py-0.5 text-[12px]" : "px-2 py-1 text-[13px]",
        due ? "border-warn bg-warn-tint text-warn" : "border-dashed border-accent-tint-2 text-accent",
      )}
    >
      {due ? "◷ " : ""}
      {title}
      {where && <span className="opacity-70"> · {where}</span>}
    </button>
  );
}

function Tray({
  title,
  items,
  ro,
  now,
  days,
  hours,
  onPlan,
}: {
  title: string;
  items: Task[];
  ro: boolean;
  now: number;
  days: string[];
  hours: number[];
  onPlan(id: string, day: string, hour: number): void;
}) {
  const { t, i18n } = useTranslation();
  const dayName = (d: string) =>
    new Date(d + "T12:00:00").toLocaleDateString(i18n.language, { weekday: "short", day: "numeric" });
  if (!items.length) return null;
  return (
    <section className="mb-3">
      <h3 className="mb-1 px-1 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
        {title}
      </h3>
      <ul className="flex flex-col gap-1">
        {items.map((x) => (
          <li
            key={x.id}
            draggable={!ro}
            onDragStart={(e) => startDrag(e.dataTransfer, x.id)}
            data-testid="planner-tray-item"
            data-task-id={x.id}
            className="relative cursor-grab rounded-sm border border-neutral-200 bg-[var(--surface)] px-2 py-1 text-[13px] dark:border-neutral-800"
          >
            <span className="block truncate">
              {x.title}
              {typeof x.props.due === "string" && (
                <span className="ml-2 text-[11px] text-neutral-500">
                  {fmtRelativeAbsolute(x.props.due, now, i18n.language, t)}
                </span>
              )}
            </span>
            {/* the route without dragging: for the keyboard, and shown on touch screens */}
            {!ro && (
              <label className="sr-only touch:not-sr-only touch:mt-1 touch:flex touch:items-center touch:gap-2 touch:text-[12px] touch:text-neutral-500">
                {t("planner.planFor")}
                <select
                  value=""
                  onChange={(e) => {
                    const [day, hour] = e.target.value.split("|");
                    if (day) onPlan(x.id, day, Number(hour));
                  }}
                  className="touch:h-11 touch:border touch:border-neutral-200 touch:bg-[var(--surface)] dark:touch:border-neutral-800 touch:min-w-0 touch:flex-1 touch:rounded-sm touch:px-2 touch:text-[13px]"
                  data-testid="planner-plan-for"
                >
                  <option value="">…</option>
                  {days.map((d) => (
                    <optgroup key={d} label={dayName(d)}>
                      {hours.map((h) => (
                        <option key={h} value={`${d}|${h}`}>
                          {days.length > 1 ? `${dayName(d)} ` : ""}
                          {String(h).padStart(2, "0")}:00
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A calendar appointment: read-only, shown so free hours are visible when planning. */
function PlannerEvent({ e, lang, compact }: { e: CalendarEvent; lang: string; compact?: boolean }) {
  const { t } = useTranslation();
  const when = e.allDay ? t("calendar.allDay") : timeRange(e, lang);
  return (
    <div
      data-testid="planner-event"
      title={compact ? `${e.title} · ${when}` : undefined}
      className={clsx(
        "flex min-w-0 items-center gap-1.5 rounded-sm border border-dashed border-neutral-300 text-neutral-600 dark:border-neutral-700 dark:text-neutral-300",
        compact ? "px-1.5 py-0.5 text-[12px]" : "gap-2 px-2 py-1 text-[13px]",
      )}
    >
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ background: `var(${e.color && e.color.startsWith("--") ? e.color : "--chart-other"})` }}
      />
      <span className="min-w-0 flex-1 truncate">{e.title}</span>
      {!compact && <span className="font-mono text-[11px] text-neutral-500">{when}</span>}
      <span className="sr-only">{t("calendar.fromCalendar", { name: e.calendar })}</span>
    </div>
  );
}
